import {
	getAwningMultiSelectParamValues,
	getAwningParamValue,
	getAwningTierPrice,
} from '../Quote/awningConfiguratorUtils';

// The awning configurator writes one parameter per configurable field. These are every field the
// configurator can produce, in the order the form asks for them, so the export header always lines
// up with the selections that were possible -- even when a given awning never set one of them.
// Multi-select fields (marked `multi`) are stored as JSON arrays and exported as a joined list.
const AWNING_PARAMETER_COLUMNS = [
	{ name: 'selectUnitType', header: 'Unit Type' },
	{ name: 'width', header: 'Width' },
	{ name: 'projection', header: 'Projection' },
	{ name: 'armsWithLed', header: 'Arms With LED' },
	{ name: 'frameColor', header: 'Frame Color' },
	{ name: 'mountingOption', header: 'Mounting Option' },
	{ name: 'roofMountBrackets', header: 'Roof Mount Brackets' },
	{ name: 'mountingBrackets', header: 'Mounting Brackets' },
	{ name: 'driveSide', header: 'Drive Side' },
	{ name: 'operation', header: 'Operation' },
	{ name: 'motorCable', header: 'Motor Cable' },
	{ name: 'remote', header: 'Remote', multi: true },
	{ name: 'crank', header: 'Crank' },
	{ name: 'desiredPitch', header: 'Desired Pitch' },
	{ name: 'fabricBrand', header: 'Fabric Brand' },
	{ name: 'fabricNumber', header: 'Fabric Number' },
	{ name: 'colorName', header: 'Fabric Color' },
];

const getAwningColumnValue = (awning, { name, multi }: { name: string; multi?: boolean }): string => (
	multi
		? getAwningMultiSelectParamValues(awning?.parameters, name).join(', ')
		: getAwningParamValue(awning?.parameters, name)
);

const BORDER = {
	left: { style: 'thin', color: 'black' },
	right: { style: 'thin', color: 'black' },
	top: { style: 'thin', color: 'black' },
	bottom: { style: 'thin', color: 'black' },
	diagonal: { style: 'thin', color: 'black' },
	diagonalDown: true,
	outline: true,
};

/**
 * Reads the sensors parameter off an awning. Sensors are stored as a JSON encoded array because the
 * configurator's sensor picker is a multi-select.
 */
export const getAwningSensors = (awning): string[] => (
	getAwningMultiSelectParamValues(awning?.parameters, 'sensors')
);

/**
 * Reads the remotes parameter off an awning. Like sensors, remotes are a multi-select stored as a
 * JSON encoded array; awnings quoted before that change hold a single plain value.
 */
export const getAwningRemotes = (awning): string[] => (
	getAwningMultiSelectParamValues(awning?.parameters, 'remote')
);

function awningRow({
	worksheet, workbook, row, awning, sensorOptions, hidePrice, currencyData
}) {
	let col = 1;

	const cellStyle = workbook.createStyle({
		alignment: {
			horizontal: 'center',
		},
		fill: {
			type: 'pattern',
			patternType: 'solid',
			fgColor: '#f0f0f0',
		},
		border: BORDER,
	});

	const currencyStyle = workbook.createStyle({
		numberFormat: '$#,##0.00; ($#,##0.00); -',
		fill: {
			type: 'pattern',
			patternType: 'solid',
			fgColor: '#f0f0f0',
		},
		border: BORDER,
	});

	const createCell = (value) => {
		worksheet
			.cell(row, col++)
			.string(`${value ?? ''}`)
			.style(cellStyle);
	};

	const boolCell = (value) => {
		worksheet
			.cell(row, col++)
			.bool(Boolean(value))
			.style(cellStyle);
	};

	const moneyCell = (amount) => {
		worksheet
			.cell(row, col++)
			.number(amount || 0)
			.style(currencyStyle);
	};

	const quantity = awning.quantity > 0 ? awning.quantity : 1;

	createCell(awning.line_number);
	createCell(awning.room_name);
	createCell(awning.shade_name || awning.name || 'Awning');
	createCell(awning.part_number);
	createCell(quantity);

	AWNING_PARAMETER_COLUMNS.forEach((column) => createCell(getAwningColumnValue(awning, column)));

	const selectedSensors = getAwningSensors(awning).map((sensor) => sensor.toUpperCase());

	sensorOptions.forEach((sensor) => boolCell(selectedSensors.includes(sensor.toUpperCase())));

	if (!hidePrice) {
		const totalMsrp = getAwningTierPrice(awning, 'msrp') * currencyData.multiplier;
		const dealerPrice = getAwningTierPrice(awning, 'dealer') * currencyData.multiplier;

		moneyCell(totalMsrp / quantity);
		moneyCell(totalMsrp);
		moneyCell(dealerPrice / quantity);
		moneyCell(dealerPrice);
	}
}

function awningsHeaderRow({ worksheet, style, row, sensorOptions, hidePrice, currencyData }) {
	let col = 1;

	const cell = (string) => {
		worksheet.cell(row, col++)
			.string(string)
			.style(style);
	};

	cell('Line #');
	cell('Room Name');
	cell('Awning Name');
	cell('Part #');
	cell('Qty');

	AWNING_PARAMETER_COLUMNS.forEach(({ header }) => cell(header));

	sensorOptions.forEach((sensor) => cell(`Sensor - ${sensor}`));

	if (!hidePrice) {
		cell(`Price (${currencyData.ISO})`);
		cell(`Total Price (${currencyData.ISO})`);
		cell(`Dealer Price (${currencyData.ISO})`);
		cell(`Total Dealer Price (${currencyData.ISO})`);
	}
}

export default function awningsSection({
	workbook, worksheet, awnings, sensorOptions, row, hidePrice, currencyData
}) {
	let currentRow = row;

	const HEAD_ROW_STYLE = workbook.createStyle({
		alignment: {
			horizontal: 'center',
		},
		fill: {
			type: 'pattern',
			patternType: 'solid',
			fgColor: '#d5d5d5',
		},
		border: BORDER,
	});

	const HEADER_STYLE = workbook.createStyle({
		font: {
			size: 16,
			bold: true,
		},
	});

	worksheet
		.cell(currentRow++, 1)
		.string('Awnings')
		.style(HEADER_STYLE);

	awningsHeaderRow({
		worksheet, style: HEAD_ROW_STYLE, row: currentRow++, sensorOptions, hidePrice, currencyData
	});

	[...(awnings ?? [])]
		.sort((a1, a2) => a1.line_number - a2.line_number)
		.forEach((awning) => awningRow({
			worksheet,
			workbook,
			row: currentRow++,
			awning,
			sensorOptions,
			hidePrice,
			currencyData,
		}));

	currentRow++;

	return currentRow;
}
